/**
 * Business Partner Compatibility — six-axis deterministic compatibility
 * report for two prospective business partners. Mirrors the v2 pattern
 * (takeaways → axes → equity → conflict signatures → negotiation prompts
 * → inner practice → methodology). No LLM, no narrative_*.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  BPCompatAxisEntry,
  BPCompatEquityEntry,
  BPCompatJointDashaEntry,
  BPCompatNegotiationPromptEntry,
  BusinessPartnerCompatRequest,
  BusinessPartnerCompatResponse,
  ChartCreateRequest,
  InnerPracticeBlockEntry,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function BusinessPartnerCompatPage({ onBack }: { onBack: () => void }) {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [partnerA, setPartnerA] = useState<ChartCreateRequest | null>(null);
  const [partnerB, setPartnerB] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<
    BusinessPartnerCompatResponse,
    Error,
    BusinessPartnerCompatRequest
  >({ mutationFn: (req) => api.businessPartnerCompat(req) });

  const onSubmit = useCallback(() => {
    if (partnerA && partnerB) {
      reportMut.mutate({ partner_a: partnerA, partner_b: partnerB });
    }
  }, [partnerA, partnerB, reportMut]);

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    if (reportMut.error instanceof ApiError && reportMut.error.status === 422) {
      return t('अमान्य इनपुट — दोनों चार्ट सत्यापित करें', 'Invalid input — verify both charts');
    }
    return reportMut.error.message ?? 'Error';
  }, [reportMut.error, lang]);

  const ready = partnerA != null && partnerB != null;
  const report = reportMut.data;

  const downloadPdf = useCallback(async () => {
    if (!partnerA || !partnerB) return;
    try {
      setPdfDownloading(true);
      const blob = await api.businessPartnerCompatPdfBlob(
        { partner_a: partnerA, partner_b: partnerB },
        lang,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bp_compat_${partnerA.name}_${partnerB.name}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [partnerA, partnerB, lang, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button"
        onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>
      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('व्यापार-साझेदारी संगति', 'Business Partner Compatibility')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'नवतारा + स्वामी-मैत्री + सम्बन्ध-अक्ष + संयुक्त-दशा + संघर्ष-संकेत — पूँजी-संरचना एवं अनुबन्ध-पूर्व चर्चा-प्रश्न।',
              'Navatāra + lord-friendship + sambandha + joint-dasha + conflict signatures — equity structure and pre-deed prompts.',
            )}
          </p>
        </div>
        <LangToggle lang={lang} setLang={setLang} />
      </header>

      <section className="space-y-5">
        <PartnerCard
          label={t('1. साथी A — जन्म विवरण', '1. Partner A — Birth Details')}
          value={partnerA}
          onSet={setPartnerA}
          t={t}
        />
        <PartnerCard
          label={t('2. साथी B — जन्म विवरण', '2. Partner B — Birth Details')}
          value={partnerB}
          onSet={setPartnerB}
          t={t}
        />
      </section>

      <button
        type="button"
        disabled={!ready || reportMut.isPending}
        onClick={onSubmit}
        className="rounded-md bg-saffron-600 px-4 py-2 font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
      >
        {reportMut.isPending
          ? t('विश्लेषण हो रहा है…', 'Analysing…')
          : t('विश्लेषण करें', 'Analyse compatibility')}
      </button>

      {submitError && (
        <p className="rounded-2xl bg-rose-950/60 p-4 text-rose-200 ring-1 ring-rose-900">
          {submitError}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
        />
      )}
    </div>
  );
}

function LangToggle({
  lang, setLang,
}: { lang: 'hi' | 'en'; setLang: (l: 'hi' | 'en') => void }) {
  return (
    <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
      <button
        type="button"
        onClick={() => setLang('hi')}
        className={
          'px-3 py-1.5 text-xs font-medium transition '
          + (lang === 'hi'
            ? 'bg-saffron-600 text-stone-950'
            : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
        }
      >
        हिन्दी
      </button>
      <button
        type="button"
        onClick={() => setLang('en')}
        className={
          'px-3 py-1.5 text-xs font-medium transition '
          + (lang === 'en'
            ? 'bg-saffron-600 text-stone-950'
            : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
        }
      >
        English
      </button>
    </div>
  );
}

function PartnerCard({
  label, value, onSet, t,
}: {
  label: string;
  value: ChartCreateRequest | null;
  onSet: (v: ChartCreateRequest) => void;
  t: (hi: string, en: string) => string;
}) {
  return (
    <div className="rounded-2xl bg-stone-900/40 p-3 ring-1 ring-stone-800">
      <h3 className="mb-2 text-sm font-semibold text-saffron-300">{label}</h3>
      <ChartInputForm onSubmit={onSet} isSubmitting={false} />
      {value && (
        <p className="mt-2 text-xs text-emerald-400">
          ✓ {t('दर्ज', 'Saved')}: {value.name}
        </p>
      )}
    </div>
  );
}

function ReportView({
  report, lang, t, onDownloadPdf, pdfDownloading,
}: {
  report: BusinessPartnerCompatResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
}) {
  const verdictColor =
    report.direction === 'go' ? 'bg-emerald-700/40 text-emerald-100 ring-emerald-700/40'
    : report.direction === 'cautious_go' ? 'bg-amber-700/40 text-amber-100 ring-amber-700/40'
    : 'bg-rose-800/50 text-rose-100 ring-rose-800/50';

  return (
    <article className="space-y-4">
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-blue-950/60 to-stone-900 p-5 ring-2 ring-blue-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-blue-300">
            {t('व्यापार-साझेदारी संगति', 'Business Partner Compatibility')}
          </p>
          <p className="mt-1 text-base font-medium text-stone-200">
            {report.input_a?.name} <span className="text-stone-500">×</span>{' '}
            {report.input_b?.name}
          </p>
          <span className={`mt-2 inline-block rounded-xl px-3 py-1 text-sm font-semibold ring-1 ${verdictColor}`}>
            {lang === 'hi' ? report.direction_label_hi : report.direction_label_en}
            {' · '}
            <span className="font-normal opacity-80">{report.overall_score.toFixed(1)} / 10</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={pdfDownloading}
          className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {pdfDownloading ? t('बन रहा है…', 'Generating…') : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </section>

      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-saffron-300">
          {t('मुख्य निष्कर्ष', 'Key Takeaways')}
        </h2>
        <ul className="ml-5 list-disc space-y-2 text-sm text-stone-200">
          {(lang === 'hi' ? report.takeaways_hi : report.takeaways_en).map((tk, i) => (
            <li key={i}>{tk}</li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-blue-300">
          {t('छह-संयोग विश्लेषण', 'Six Compatibility Axes')}
        </h2>
        {report.axes.map((ax) => <AxisCard key={ax.axis_id} axis={ax} lang={lang} t={t} />)}
      </section>

      <EquitySection equity={report.equity} lang={lang} t={t} />

      {report.joint_dasha && (
        <JointDashaSection jd={report.joint_dasha} lang={lang} t={t} />
      )}

      {report.conflict_signatures_en.length > 0 && (
        <section className="rounded-2xl bg-rose-950/30 p-5 ring-1 ring-rose-900/50">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-rose-300">
            {t('संघर्ष-संकेत', 'Conflict Signatures')}
          </h2>
          <ul className="ml-5 list-disc space-y-2 text-sm text-rose-100">
            {(lang === 'hi' ? report.conflict_signatures_hi : report.conflict_signatures_en).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-saffron-300">
          {t('अनुबन्ध-पूर्व चर्चा-प्रश्न', 'Pre-Deed Negotiation Prompts')}
        </h2>
        {report.negotiation_prompts.map((pr, i) => (
          <NegotiationCard key={i} prompt={pr} lang={lang} t={t} />
        ))}
      </section>

      {report.inner_practice && (
        <InnerPracticeSection ip={report.inner_practice} lang={lang} t={t} />
      )}

      <section className="rounded-2xl bg-stone-900/40 p-4 text-xs text-stone-500 ring-1 ring-stone-800">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500">
          {t('विधि एवं स्रोत', 'Methodology & Sources')}
        </h3>
        <ul className="space-y-1 leading-relaxed">
          {report.methods.map((m, i) => (
            <li key={i}>
              <span>{lang === 'hi' ? m.method_hi : m.method_en}</span>
              <span className="text-stone-600 italic"> — {lang === 'hi' ? m.source_hi : m.source_en}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 italic text-stone-600">
          {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
        </p>
        <p className="mt-1 text-[9px] text-stone-700">{report.rules_version}</p>
      </section>
    </article>
  );
}

function AxisCard({
  axis, lang, t,
}: {
  axis: BPCompatAxisEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const tone = axis.score >= 7 ? 'border-emerald-700/40 bg-emerald-950/20'
    : axis.score >= 5 ? 'border-blue-700/40 bg-blue-950/20'
    : axis.score >= 3 ? 'border-amber-700/40 bg-amber-950/20'
    : 'border-rose-700/40 bg-rose-950/20';
  const scoreTone = axis.score >= 7 ? 'bg-emerald-800/60 text-emerald-100'
    : axis.score >= 5 ? 'bg-blue-800/60 text-blue-100'
    : axis.score >= 3 ? 'bg-amber-800/60 text-amber-100'
    : 'bg-rose-800/60 text-rose-100';
  return (
    <div className={`rounded-2xl border-l-4 p-4 ring-1 ring-stone-800 ${tone}`}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-stone-100">
          {lang === 'hi' ? axis.label_hi : axis.label_en}
        </h3>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${scoreTone}`}>
          {axis.score.toFixed(1)} / 10
        </span>
      </div>
      <p className="mb-2 text-sm leading-relaxed text-stone-200">
        {lang === 'hi' ? axis.reading_hi : axis.reading_en}
      </p>
      <p className="rounded-md bg-stone-900/60 p-2 text-xs italic leading-relaxed text-stone-300">
        <strong className="not-italic text-stone-200">
          {t('प्रबन्धन', 'Management')}:
        </strong>{' '}
        {lang === 'hi' ? axis.management_hi : axis.management_en}
      </p>
    </div>
  );
}

function EquitySection({
  equity, lang, t,
}: {
  equity: BPCompatEquityEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-amber-950/30 p-5 ring-1 ring-amber-800/40">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-amber-200">
        {t('पूँजी एवं भूमिका-सिफ़ारिश', 'Equity & Role Recommendation')}
      </h2>
      <p className="text-base font-semibold text-amber-100">
        {lang === 'hi' ? equity.suggested_split_hi : equity.suggested_split_en}
      </p>
      <p className="mt-2 text-sm text-stone-300">
        <strong>{t('मुख्य-नेता', 'Primary mover')}:</strong>{' '}
        {lang === 'hi' ? equity.primary_mover_label_hi : equity.primary_mover_label_en}
      </p>
      <p className="mt-2 text-xs italic leading-relaxed text-stone-400">
        {lang === 'hi' ? equity.rationale_hi : equity.rationale_en}
      </p>
    </section>
  );
}

function JointDashaSection({
  jd, lang, t,
}: {
  jd: BPCompatJointDashaEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-indigo-950/30 p-5 ring-1 ring-indigo-800/40">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-indigo-200">
        {t('संयुक्त-दशा संयोग', 'Joint-Dasha Window')}
      </h2>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-md bg-stone-900/60 p-3">
          <p className="text-xs uppercase tracking-wider text-stone-500">
            {t('साथी A', 'Partner A')}
          </p>
          <p className="mt-1 font-semibold text-stone-200">
            {lang === 'hi' ? jd.partner_a_md_hi : jd.partner_a_md.toUpperCase()}
          </p>
          <p className="text-xs text-stone-400">{jd.a_disposition}</p>
        </div>
        <div className="rounded-md bg-stone-900/60 p-3">
          <p className="text-xs uppercase tracking-wider text-stone-500">
            {t('साथी B', 'Partner B')}
          </p>
          <p className="mt-1 font-semibold text-stone-200">
            {lang === 'hi' ? jd.partner_b_md_hi : jd.partner_b_md.toUpperCase()}
          </p>
          <p className="text-xs text-stone-400">{jd.b_disposition}</p>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-indigo-100">
        {lang === 'hi' ? jd.joint_verdict_hi : jd.joint_verdict_en}
      </p>
    </section>
  );
}

function NegotiationCard({
  prompt, lang, t: _t,
}: {
  prompt: BPCompatNegotiationPromptEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <div className="rounded-2xl border-l-4 border-saffron-600 bg-stone-900/40 p-4 ring-1 ring-stone-800">
      <h3 className="mb-2 text-sm font-semibold text-saffron-200">
        {lang === 'hi' ? prompt.topic_hi : prompt.topic_en}
      </h3>
      <ul className="ml-5 list-disc space-y-1 text-sm text-stone-300">
        {(lang === 'hi' ? prompt.questions_hi : prompt.questions_en).map((q, i) => (
          <li key={i}>{q}</li>
        ))}
      </ul>
    </div>
  );
}

function InnerPracticeSection({
  ip, lang, t,
}: {
  ip: InnerPracticeBlockEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-emerald-950/30 p-5 ring-1 ring-emerald-800/40">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-emerald-200">
        {t('संयुक्त साधना (Inner Practice)', 'Joint Inner Practice')}
      </h2>

      <div className="rounded-md bg-stone-900/40 p-3 ring-1 ring-stone-800">
        <p className="text-[10px] uppercase tracking-widest text-emerald-400">
          {t('इष्ट-देव जाप', 'Iṣṭa-deva recitation')}
        </p>
        <p className="mt-1 text-sm text-stone-200">
          {lang === 'hi' ? ip.ishta_deva.text_hi : ip.ishta_deva.text_en}
        </p>
      </div>

      {ip.bhavana_contemplations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400">
            {t('चयनित भावनाएँ', 'Selected Bhāvanās')}
          </p>
          {ip.bhavana_contemplations.map((bv) => (
            <div key={bv.bhavana_id} className="rounded-md bg-stone-900/40 p-3 ring-1 ring-stone-800">
              <p className="text-sm font-semibold text-stone-200">
                {lang === 'hi' ? bv.title_hi : bv.title_en}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-stone-400">
                {lang === 'hi' ? bv.theme_hi : bv.theme_en}
              </p>
              <p className="mt-2 text-sm italic text-emerald-300">
                "{lang === 'hi' ? bv.affirmation_hi : bv.affirmation_en}"
              </p>
            </div>
          ))}
        </div>
      )}

      {ip.affirmations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400">
            {t('भावना-योग संकल्प', 'Bhāvanā Yoga Affirmations')}
          </p>
          {ip.affirmations.map((af, i) => (
            <div key={i} className="rounded-md bg-stone-900/40 p-3 ring-1 ring-stone-800">
              <p className="text-[10px] uppercase tracking-wider text-stone-500">
                {lang === 'hi' ? af.chart_signal_hi : af.chart_signal_en}
              </p>
              <p className="mt-1 text-sm italic text-emerald-200">
                "{lang === 'hi' ? af.affirmation_hi : af.affirmation_en}"
              </p>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 rounded-md bg-stone-900/40 p-2 text-xs italic text-stone-300">
        {lang === 'hi' ? ip.practice_instruction_hi : ip.practice_instruction_en}
      </p>
    </section>
  );
}
